import { createTranslator, type Locale, resolveLocale, type Translator } from '@telestore/i18n';
import { themeToCssVariables } from '@telestore/theming';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setApiLocale, type ShopPayload } from './api.js';
import { telegramInitData, telegramUser } from './telegram.js';

/**
 * Shop context: the resolved theme, the locale, and the translator.
 *
 * Theme tokens are written onto `document.documentElement` rather than passed
 * through props — §5 requires that a template change repaints the whole
 * storefront with no build and no deploy, and CSS variables are how that
 * happens.
 */

interface ShopContextValue {
  data: ShopPayload;
  locale: Locale;
  setLocale(locale: Locale): void;
  t: Translator;
  /**
   * This shop's record of whoever opened the Mini App, or null on plain web.
   *
   * Resolved from `initData` on start, which is also what CREATES the record
   * the first time (§3 `customer`). Checkout uses it to prefill, so a returning
   * buyer does not retype a phone number they already gave this shop.
   */
  customer: { id: string; name: string | null; phone: string | null } | null;
}

const ShopContext = createContext<ShopContextValue | null>(null);

export function useShop(): ShopContextValue {
  const value = useContext(ShopContext);
  if (!value) throw new Error('useShop must be used inside <ShopProvider>');
  return value;
}

const LOCALE_KEY = 'telestore.locale';

export function ShopProvider({ data, children }: { data: ShopPayload; children: ReactNode }) {
  const [customer, setCustomer] = useState<ShopContextValue['customer']>(null);
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale({
      explicit: typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_KEY) : null,
      telegramLanguageCode: telegramUser()?.language_code ?? null,
      shopDefault: data.shop.defaultLocale,
    }),
  );

  useEffect(() => {
    setApiLocale(locale);
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch {
      // non-fatal
    }
  }, [locale]);

  /**
   * Inject the resolved design tokens (§5.1), in the scheme this device is
   * actually asking for (§5 dark-mode-aware templates, v2).
   *
   * The listener matters more than it looks: Android switches to dark at
   * sunset while the Mini App is open, and a storefront that only reads the
   * preference once is half-dark for the rest of the evening.
   */
  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const theme = data.resolvedTheme;

    const apply = () => {
      const scheme =
        theme.colorScheme === 'auto'
          ? media.matches
            ? 'dark'
            : 'light'
          : theme.colorScheme;

      for (const [key, value] of Object.entries(themeToCssVariables(theme, scheme))) {
        root.style.setProperty(key, value);
      }
      root.style.colorScheme = scheme;
      const tokens = scheme === 'dark' ? theme.darkTokens : theme.tokens;
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', tokens['color-bg']);
    };

    apply();
    document.title = data.shop.name;

    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [data]);

  // Load only the fonts this template actually needs — §5 keeps the list
  // curated and Khmer-safe precisely so this stays one small request.
  useEffect(() => {
    const families = fontFamiliesFor(data.resolvedTheme.tokens['font-heading'], data.resolvedTheme.tokens['font-body']);
    if (families.length === 0) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families
      .map((family) => `family=${family}`)
      .join('&')}&display=swap`;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [data.resolvedTheme]);

  /**
   * Say who we are, once, on start.
   *
   * Failing is not an error the customer should ever see: §2 makes the
   * storefront dual-mode, and everything below here has to work for a visitor
   * who arrived from a Facebook link with no Telegram at all.
   */
  useEffect(() => {
    const initData = telegramInitData();
    if (!initData) return;
    let cancelled = false;
    api
      .identifyTelegram(data.shop.slug, initData)
      .then((result) => {
        if (!cancelled) setCustomer(result.customer);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [data.shop.slug]);

  const value = useMemo<ShopContextValue>(
    () => ({
      data,
      locale,
      setLocale: setLocaleState,
      t: createTranslator(locale),
      customer,
    }),
    [data, locale, customer],
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

/** Map the CSS font stacks back to the Google Fonts families they need. */
function fontFamiliesFor(heading: string, body: string): string[] {
  const wanted = new Set<string>();
  const stacks = `${heading} ${body}`;
  if (stacks.includes('Kantumruy Pro')) wanted.add('Kantumruy+Pro:wght@300;400;500;600;700');
  if (stacks.includes('Noto Sans Khmer')) wanted.add('Noto+Sans+Khmer:wght@300;400;500;600;700');
  if (stacks.includes('Noto Serif Khmer')) wanted.add('Noto+Serif+Khmer:wght@400;600;700');
  if (stacks.includes('Battambang')) wanted.add('Battambang:wght@300;400;700');
  if (stacks.includes('Moul')) wanted.add('Moul');
  return [...wanted];
}

export function useShopData(slug: string) {
  const [state, setState] = useState<{
    status: 'loading' | 'ready' | 'error';
    data?: ShopPayload;
    error?: string;
  }>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    api
      .shop(slug)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'ready', data });
        /**
         * A renamed shop keeps its old slug alive forever (§6.8); rewrite the
         * address bar so what the customer copies from here is the current one.
         */
        if (data.redirectedFrom) {
          window.history.replaceState(
            null,
            '',
            window.location.pathname.replace(`/s/${data.redirectedFrom}`, `/s/${data.shop.slug}`),
          );
        }
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ status: 'error', error: error.message });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return state;
}
